import type { FrequencyEnum, LibrarySearchEntity, ProxyOption, ProxyTypeEnum, SimpleFund } from 'venn-api';
import { SupportedErrorCodes } from 'venn-api';
import { Match } from 'effect';
import {
  type AnyDuringEslintMigration,
  FrequencyTypes,
  getFrequencyType,
  getNumberOfDatapoints,
  itemHasNoReturns,
  MOMENT_DURATIONS,
  FS,
  getExtrapolateFromValue,
  type ProxyTypeValue,
  compareFrequencies,
} from 'venn-utils';
import moment from 'moment';
import {
  DisabledProxyReason,
  type FundToProxy,
  type InvestmentInfo,
  type LayerProxyInfo,
  type ProxyMetaWithName,
} from '../types';
import { getTextThemeProvider, getHelpCenterLinks } from 'venn-ui-kit';
import { isNumber } from 'lodash';

/**
 * Resolves a disabled-proxy reason to display text, applying brand-aware copy for the reasons whose
 * static enum value embeds a brand term (which the enum itself cannot express per theme).
 */
export const resolveDisabledProxyReasonMessage = (
  reason: DisabledProxyReason | string | undefined,
): string | undefined =>
  reason === DisabledProxyReason.SUBSTITUTE_SYSTEM_FUND
    ? `Unavailable for ${getTextThemeProvider().brandProvidedLabel} investments`
    : reason;

export const getProxyTypeErrorMessageWithType = (reason: DisabledProxyReason, proxyType: ProxyTypeEnum): string =>
  Match.value({ reason, proxyType }).pipe(
    Match.when(
      { reason: DisabledProxyReason.BACKFILL_QUARTERLY },
      () => 'Backfill is unavailable for quarterly investments',
    ),
    Match.when(
      { reason: DisabledProxyReason.INTERPOLATE_NONQUARTERLY },
      () => 'Interpolation is unavailable for monthly/daily investments',
    ),
    Match.when(
      { reason: DisabledProxyReason.UNAVAILABLE_DAILY, proxyType: 'DESMOOTH' },
      () => 'Desmoothing is unavailable for daily investments',
    ),
    Match.when(
      { reason: DisabledProxyReason.UNAVAILABLE_DAILY, proxyType: 'INTERPOLATE' },
      () => 'Interpolation is unavailable for daily investments',
    ),
    Match.when(
      { reason: DisabledProxyReason.UNAVAILABLE_DAILY, proxyType: 'DESMOOTH_INTERPOLATE' },
      () => 'Interpolation is unavailable for daily investments',
    ),
    Match.when(
      { reason: DisabledProxyReason.SUBSTITUTE_SYSTEM_FUND },
      () => `Substitution is unavailable for ${getTextThemeProvider().brandProvidedLabel} investments`,
    ),
    Match.when(
      { reason: DisabledProxyReason.EXTRAPOLATE_QUARTERLY },
      () => 'Extrapolation is unavailable for quarterly investments',
    ),
    Match.orElse(({ reason }) => reason),
  );

export interface ReturnsRangeInfo {
  start?: number;
  end?: number;
  frequency?: FrequencyEnum;
}

export interface CategoryProxyTypeMetadata {
  helpLink: string;
  classPrefix: string;
}

export interface SelectedProxy {
  id: string;
  name: string;
}

export const typeSupportsDesmooth = (proxyType: ProxyTypeEnum) =>
  proxyType === 'DESMOOTH' || proxyType === 'DESMOOTH_INTERPOLATE';

export const typeSupportsInterpolate = (proxyType: ProxyTypeEnum) =>
  proxyType === 'INTERPOLATE' || proxyType === 'DESMOOTH_INTERPOLATE';

export const typeSupportsCategoryPicker = (proxyType: ProxyTypeEnum) =>
  typeSupportsDesmooth(proxyType) || typeSupportsInterpolate(proxyType);

export const getReturnsRangeFromError = (error: unknown): ReturnsRangeInfo | null =>
  (error as AnyDuringEslintMigration)?.content?.code === SupportedErrorCodes.NoFundReturns ? {} : null;

export const sortCategoryOptions = (a: ProxyOption, b: ProxyOption) => {
  if (a.category.displayName && b.category.displayName) {
    return a.category.displayName.localeCompare(b.category.displayName);
  }
  if (a.valid && !b.valid) {
    return -1;
  }
  if (b.valid && !a.valid) {
    return 1;
  }
  return 0;
};

/**
 * Calculates the effective returns range after applying a backfill proxy.
 * Used for validating second layer proxy options based on the backfilled data range.
 *
 * @param rawInvestmentRange - The original/unproxied investment returns range
 * @param firstLayerProxyRange - The returns range of the first layer backfill proxy
 * @returns The effective range after backfill (start extended to proxy start, end unchanged)
 */
export const calculateBackfilledRange = (
  rawInvestmentRange: ReturnsRangeInfo | null,
  firstLayerProxyRange: ReturnsRangeInfo | null | undefined,
): ReturnsRangeInfo | null => {
  if (!rawInvestmentRange || !firstLayerProxyRange?.start) {
    return rawInvestmentRange;
  }

  return {
    start:
      rawInvestmentRange.start !== undefined
        ? Math.min(firstLayerProxyRange.start, rawInvestmentRange.start)
        : firstLayerProxyRange.start,
    end: rawInvestmentRange.end,
    frequency: rawInvestmentRange.frequency,
  };
};

const INTERPOLATION_DATA_THRESHOLD = 12;
const EXRAPOLATION_DATA_THRESHOLDS: { [K in FrequencyEnum]: number } = {
  DAILY: 90,
  WEEKLY: 26,
  MONTHLY: 24,
  QUARTERLY: 12,
  YEARLY: 0,
  UNKNOWN: 0,
};

const getDesmoothInterpolateFrequencyError = (
  proxyType: 'DESMOOTH' | 'DESMOOTH_INTERPOLATE' | 'INTERPOLATE',
  rawProxyReturnsRange?: ReturnsRangeInfo | null,
  rawInvestmentReturnsRange?: ReturnsRangeInfo | null,
): DisabledProxyReason | undefined => {
  const message =
    proxyType === 'DESMOOTH' ? DisabledProxyReason.DESMOOTH_LOWER_FREQ : DisabledProxyReason.INTERPOLATE_LOWER_FREQ;

  if (!rawProxyReturnsRange?.frequency || !rawInvestmentReturnsRange?.frequency) {
    return undefined;
  }

  return rawInvestmentReturnsRange?.frequency <= rawProxyReturnsRange?.frequency ? message : undefined;
};

/**
 * Determine whether a proxy selection should be disabled and the reason to disable it.
 * @param proxyType proxy method to use
 * @param investment the investment to proxy
 * @param rawInvestmentReturnsRange unproxied returns info of the investment being proxied, null if unavailable
 * @param rawProxyReturnsRange unproxied returns info of the investment to be used as a proxy, null if unavailable
 * @return the reason to disable the proxy type, null if it shouldn't be disabled
 */
export const getDisabledProxyTypeMessage = (
  proxyType: ProxyTypeEnum,
  investment: SimpleFund | FundToProxy,
  rawInvestmentReturnsRange: ReturnsRangeInfo | null,
  rawProxyReturnsRange: ReturnsRangeInfo | undefined | null,
): DisabledProxyReason | undefined => {
  if (investment.assetType === 'BENCHMARK') {
    return DisabledProxyReason.COMPOSITE_BENCHMARK_NOT_PROXYABLE;
  }

  const { start: proxyStart, end: proxyEnd, frequency: proxyFrequency } = rawProxyReturnsRange ?? {};
  if (rawProxyReturnsRange && itemHasNoReturns(proxyStart, proxyEnd)) {
    return DisabledProxyReason.PROXY_NO_RETURNS;
  }

  const enableInterpolationMonthlyFf = FS.has('enable_interpolation_monthly_ff');
  const isInterpolatable =
    ('interpolatable' in investment && investment.interpolatable) ||
    rawInvestmentReturnsRange?.frequency === 'QUARTERLY' ||
    (enableInterpolationMonthlyFf && rawInvestmentReturnsRange?.frequency === 'MONTHLY');
  // TODO(VER-816, VENN-28438): get this from the backend proxy metadata endpoint instead if it is cheap for the backend, so we can
  // dedupe this error-prone logic.
  const hasMinDataForInterpolation = () =>
    ('hasMinDataForInterpolation' in investment && investment.hasMinDataForInterpolation) ||
    (rawInvestmentReturnsRange?.start && rawInvestmentReturnsRange?.end && rawInvestmentReturnsRange?.frequency
      ? getNumberOfDatapoints(
          rawInvestmentReturnsRange.start,
          rawInvestmentReturnsRange.end,
          rawInvestmentReturnsRange.frequency,
        ) >= INTERPOLATION_DATA_THRESHOLD
      : false);

  switch (proxyType) {
    case 'EXTRAPOLATE':
      if (rawInvestmentReturnsRange?.frequency === 'QUARTERLY') {
        return DisabledProxyReason.EXTRAPOLATE_QUARTERLY;
      }
      if (
        rawProxyReturnsRange?.frequency &&
        rawInvestmentReturnsRange?.frequency &&
        compareFrequencies(rawInvestmentReturnsRange.frequency, rawProxyReturnsRange.frequency) === 1
      ) {
        return DisabledProxyReason.EXTRAPOLATE_LOWER_FREQ;
      }
      if (
        rawProxyReturnsRange?.end &&
        rawInvestmentReturnsRange?.end &&
        rawProxyReturnsRange.end <= rawInvestmentReturnsRange.end
      ) {
        return DisabledProxyReason.EXTRAPOLATE_DOES_NOT_EXTEND;
      }
      if (
        rawInvestmentReturnsRange?.frequency &&
        rawInvestmentReturnsRange.start &&
        rawInvestmentReturnsRange.end &&
        getNumberOfDatapoints(
          rawInvestmentReturnsRange.start,
          rawInvestmentReturnsRange.end,
          rawInvestmentReturnsRange.frequency,
        ) < EXRAPOLATION_DATA_THRESHOLDS[rawInvestmentReturnsRange.frequency]
      ) {
        return DisabledProxyReason.EXTRAPOLATE_NOT_ENOUGH_DATA;
      }
      return undefined;
    case 'BACKFILL':
      // disable if returns info isn't available (yet)
      if (!rawInvestmentReturnsRange) {
        return DisabledProxyReason.INVESTMENT_RETURNS_UNAVAILABLE;
      }
      if (
        !FS.has('enable_backfill_quarterly_ff') &&
        isInterpolatable &&
        rawInvestmentReturnsRange?.frequency === 'QUARTERLY'
      ) {
        return DisabledProxyReason.BACKFILL_QUARTERLY;
      }

      const {
        start: investmentStart,
        end: investmentEnd,
        frequency: investmentFrequency = FrequencyTypes.DAILY, // backend default for investments with no returns
      } = rawInvestmentReturnsRange;
      const investmentFrequencyType = getFrequencyType(investmentFrequency);

      // if there's no selected proxy, allow backfilling
      if (!rawProxyReturnsRange) {
        return undefined;
      }

      const isProxyFrequentEnough = getFrequencyType(proxyFrequency) <= investmentFrequencyType; // See FrequencyTypes: Daily = 1, Weekly = 2, ...
      if (!isProxyFrequentEnough) {
        return DisabledProxyReason.BACKFILL_TOO_INFREQUENT;
      }

      if (itemHasNoReturns(investmentStart, investmentEnd)) {
        return undefined;
      }

      return (
        // proxy should have earlier data than the investment, have data at least until
        // right before the investment (i.e. no gaps after backfilling)
        proxyStart! < investmentStart! && proxyEnd! >= getPreviousDate(investmentStart!, investmentFrequencyType)
          ? undefined
          : DisabledProxyReason.BACKFILL_INSUFFICIENT_HISTORY
      );
    case 'SUBSTITUTE':
      return investment.investmentSource === 'VENN' ? DisabledProxyReason.SUBSTITUTE_SYSTEM_FUND : undefined;
    case 'INTERPOLATE':
      return !isInterpolatable
        ? enableInterpolationMonthlyFf
          ? DisabledProxyReason.UNAVAILABLE_DAILY
          : DisabledProxyReason.INTERPOLATE_NONQUARTERLY
        : !hasMinDataForInterpolation()
          ? DisabledProxyReason.INTERPOLATE_NOT_ENOUGH_DATA
          : getDesmoothInterpolateFrequencyError(proxyType, rawProxyReturnsRange, rawInvestmentReturnsRange);
    case 'DESMOOTH':
      return rawInvestmentReturnsRange?.frequency === 'DAILY'
        ? DisabledProxyReason.UNAVAILABLE_DAILY
        : !hasMinDataForInterpolation()
          ? DisabledProxyReason.DESMOOTH_NOT_ENOUGH_DATA
          : getDesmoothInterpolateFrequencyError(proxyType, rawProxyReturnsRange, rawInvestmentReturnsRange);
    case 'DESMOOTH_INTERPOLATE':
      return !isInterpolatable
        ? enableInterpolationMonthlyFf
          ? DisabledProxyReason.UNAVAILABLE_DAILY
          : DisabledProxyReason.INTERPOLATE_NONQUARTERLY
        : !hasMinDataForInterpolation()
          ? DisabledProxyReason.INTERPOLATE_NOT_ENOUGH_DATA
          : getDesmoothInterpolateFrequencyError(proxyType, rawProxyReturnsRange, rawInvestmentReturnsRange);
    default:
      return undefined;
  }
};

export const getCategoryProxyTypeMetadata = (proxyType: ProxyTypeEnum): CategoryProxyTypeMetadata => {
  switch (proxyType) {
    case 'INTERPOLATE':
      return {
        helpLink: getHelpCenterLinks().INTERPOLATION_EXTRAPOLATION_FAQ_HREF,
        classPrefix: 'interpolation',
      };
    case 'DESMOOTH':
      return {
        helpLink: getHelpCenterLinks().DESMOOTHING_FAQ_HREF,
        classPrefix: 'desmooth',
      };
    case 'DESMOOTH_INTERPOLATE':
      return {
        helpLink: getHelpCenterLinks().DESMOOTHING_FAQ_HREF,
        classPrefix: 'desmooth-interpolation',
      };
    default:
      return { helpLink: '/', classPrefix: 'unknown' };
  }
};

export const getDisabledProxyMessage = (
  potentialProxy: LibrarySearchEntity,
  proxyType: ProxyTypeEnum,
  investment: SimpleFund | FundToProxy,
  rawInvestmentReturnsRange: ReturnsRangeInfo | null,
): DisabledProxyReason | undefined => {
  return getDisabledProxyTypeMessage(proxyType, investment, rawInvestmentReturnsRange, {
    start: potentialProxy.unproxiedStartRange,
    end: potentialProxy.unproxiedEndRange,
    frequency: potentialProxy.unproxiedFrequency,
  });
};

const getPreviousDate = (date: number, frequency: FrequencyTypes): number => {
  const dateMoment = moment.utc(date);
  return dateMoment.subtract(1, MOMENT_DURATIONS[frequency]).valueOf();
};

const DESMOOTH_PROXY_TYPES = ['DESMOOTH', 'DESMOOTH_INTERPOLATE'] as const;

/**
 * Builds a ProxyMeta object for a given investment and proxy configuration.
 * This is a pure function that can be easily tested.
 */
export const buildProxyMeta = ({
  investmentId,
  proxyInfo,
  proxyOrder,
  hasSingleProxy,
  investmentInfo,
  proxySetId,
}: {
  investmentId: string;
  proxyInfo: LayerProxyInfo;
  proxyOrder: 0 | 1;
  hasSingleProxy: boolean;
  investmentInfo: InvestmentInfo;
  proxySetId: string | null | undefined;
}): ProxyMetaWithName | null => {
  if (!proxyInfo.selectedProxy) {
    return null;
  }

  const getNumLags = (): number => {
    if (hasSingleProxy && DESMOOTH_PROXY_TYPES.includes(proxyInfo.proxyType as (typeof DESMOOTH_PROXY_TYPES)[number])) {
      if (isNumber(proxyInfo.numLags) && proxyInfo.numLags > 0) {
        return proxyInfo.numLags;
      }
      return investmentInfo[investmentId]?.suggestedLags ?? 0;
    }
    return 0;
  };

  return {
    proxyId: proxyInfo.selectedProxy.id,
    fundId: investmentId,
    proxyType: proxyInfo.proxyType,
    proxyVariant: 'RETURN' as const,
    extrapolate: getExtrapolateFromValue(proxyInfo as unknown as ProxyTypeValue),
    numLags: getNumLags(),
    source: 'APP' as const,
    proxySetId: proxySetId ?? null,
    proxyOrder,
    proxyName: proxyInfo.selectedProxy.name,
  };
};
